import { DEFAULT_ERROR_MESSAGE } from "@/common/constant";
import { tableSchema } from "@/common/store/tableStore";
import {
  formatDateKST,
  formatDateOnlyKST,
  formatHour12,
} from "@/common/utils/date";
import type { FilterOption } from "@/components/designSystem/FilterSelect";
import { pricePolicyEmbedSchema } from "@/pages/pricePolicy/type/pricePolicyType";
import { cpoIdSchema } from "@/api/affiliationType";
import { z } from "zod";
import type { badgeVariantProps } from "@/components/ui/badge";

// ============================================
// Enums
// ============================================

/** CSMS 상태 (충전 상태) - BE CsmsStatus 매핑 */
export const csmsStatusSchema = z.enum([
  "Available",
  "Preparing",
  "Charging",
  "ChargeComplete",
  "Faulted",
  "Unavailable",
  // 구 Disconnected 자리. 통신장애는 통신 상태 축으로 옮겨갔다
  "NoStatus",
]);
export type CsmsStatus = z.infer<typeof csmsStatusSchema>;

/** CSMS 상태 라벨 */
export const CSMS_STATUS_LABELS: Record<CsmsStatus, string> = {
  Available: "충전가능",
  Preparing: "준비중",
  Charging: "충전중",
  ChargeComplete: "충전완료",
  Faulted: "고장",
  Unavailable: "사용불가",
  NoStatus: "상태없음",
};

/** CSMS 상태에 따른 뱃지 스타일 */
export const csmsStatusStyles: Record<
  CsmsStatus,
  { variant: badgeVariantProps; className?: string }
> = {
  Available: { variant: "available" },
  Preparing: { variant: "caution" },
  Charging: { variant: "positive" },
  ChargeComplete: { variant: "caution" },
  Faulted: { variant: "urgent" },
  Unavailable: { variant: "urgent" },
  // 상태가 아니라 부재라서 색을 주지 않는다
  NoStatus: { variant: "none" },
};

/** 통신 상태 */
export const connectionStatusSchema = z.enum([
  "Connected",
  "Disconnected",
  "Waiting",
]);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

export const CONNECTION_STATUS_LABELS: Record<ConnectionStatus, string> = {
  Connected: "연결",
  Disconnected: "통신장애",
  Waiting: "연결대기",
};

/** 통신 상태 앞에 찍는 램프 색 (토큰명) */
export const CONNECTION_STATUS_DOT_COLORS: Record<ConnectionStatus, string> = {
  Connected: "status-available",
  Disconnected: "status-urgent",
  Waiting: "labels-disable",
};

export const CONNECTION_STATUS_OPTIONS: FilterOption[] =
  connectionStatusSchema.options.map((value) => ({
    value,
    label: CONNECTION_STATUS_LABELS[value],
  }));

/** 충전 상태를 믿을 수 없는 이유. BE 는 이유만 내리고 문구는 FE 가 갖는다 */
export const staleReasonSchema = z.enum(["Disconnected", "ReconnectPending"]);
export type StaleReason = z.infer<typeof staleReasonSchema>;

export const STALE_REASON_TITLE = "최신 상태 확인 필요";

export const STALE_REASON_MESSAGES: Record<StaleReason, string> = {
  Disconnected: "현재 통신이 끊긴 상태여서 실제 충전 상태와 다를 수 있어요.",
  ReconnectPending:
    "통신이 끊겼다가 다시 연결된 이후, 최신 충전상태를 아직 수신하지 못했어요.",
};

/** OCPP 상태 */
export const ocppStatusSchema = z.enum([
  "Available",
  "Preparing",
  "Reserved",
  "Charging",
  "Finishing",
  "SuspendedEV",
  "SuspendedEVSE",
  "Faulted",
  "Unavailable",
]);
export type OcppStatus = z.infer<typeof ocppStatusSchema>;

/** 충전기 운영 상태 - BE ChargerStatus 매핑 */
export const chargerStatusSchema = z.enum(["OPEN", "CLOSE", "PREPARE"]);
export type ChargerStatus = z.infer<typeof chargerStatusSchema>;

/** 공용 여부는 API 가 boolean 이지만 Radio UI 는 문자열만 다룬다 */
export const chargerPublicValueSchema = z.enum(["true", "false"]);

export const CHARGER_PUBLIC_OPTIONS: FilterOption[] = [
  { value: "true", label: "공용" },
  { value: "false", label: "비공용" },
];

/** 검색어 검색 타입 - BE KeywordSearchType 매핑 */
export const chargerSearchTypeSchema = z.enum([
  "managed_id", // NOTI ! : 관리 번호 26.05.12 추가
  "csms_charger_id",
  "station_name",
  "charger_vendor_name",
  "charger_model_name",
  "connector_shape",
  "serial_number",
  "gov_connector_id",
  "price_policy_name",
]);
export type ChargerSearchType = z.infer<typeof chargerSearchTypeSchema>;

// ============================================
// Charger List Request
// ============================================
export const requestChargerSchema = z.object({
  // 검색어
  search_type: chargerSearchTypeSchema.nullish(),
  keyword: z.string().max(100).nullish(),
  // 기간 검색 (BE 지원: installed_on, activated_on, created_at, updated_at)
  from_created_at: z.string().nullish(),
  to_created_at: z.string().nullish(),
  from_installed_on: z.string().nullish(),
  to_installed_on: z.string().nullish(),
  from_activated_on: z.string().nullish(),
  to_activated_on: z.string().nullish(),
  from_updated_at: z.string().nullish(),
  to_updated_at: z.string().nullish(),
  from_last_charged_at: z.string().nullish(),
  to_last_charged_at: z.string().nullish(),
  from_last_heartbeat_at: z.string().nullish(),
  to_last_heartbeat_at: z.string().nullish(),
  from_status_updated_at: z.string().nullish(),
  to_status_updated_at: z.string().nullish(),
  // 필터
  station_filter: z.array(z.string()).nullish(),
  vendor_name_filter: z.array(z.string()).nullish(),
  model_name_filter: z.array(z.string()).nullish(),
  status_filter: z.array(csmsStatusSchema).nullish(),
  connection_status_filter: z.array(connectionStatusSchema).nullish(),
  is_public_filter: z.array(chargerPublicValueSchema).nullish(),
  /** 조치 필요 충전기만. status_filter 와 함께 보내면 서버가 422 로 막는다 */
  action_required: z.boolean().nullish(),
  has_gov_id_filter: z.array(z.boolean()).nullish(),
  charging_speed_filter: z.array(z.string()).nullish(),
  // 소유 운영사 필터 — MASTER 전용
  cpo_ids: z.array(z.number()).nullish(),
});
export type RequestChargerSchema = z.infer<typeof requestChargerSchema>;

export const requestChargerListSchema = tableSchema.extend(
  requestChargerSchema.shape,
);
export type RequestChargerList = z.infer<typeof requestChargerListSchema>;

// ============================================
// Charger List Response (커넥터 기준)
// ============================================

/** 소유 운영사. 고객사 응답은 `id` 가 null 이다 — 운영사명을 눌러 열 화면이 없다 */
export const chargerCpoSchema = z.object({
  id: cpoIdSchema.nullish(),
  name: z.string(),
});

/** 충전소 정보 (리스트용). 고객사 응답은 `uuid` 가 null 이다 — 충전소명이 링크가 아니다 */
export const stationInfoSchema = z.object({
  uuid: z.string().nullish(),
  name: z.string(),
});
export type StationInfo = z.infer<typeof stationInfoSchema>;

/** 충전기 정보 (리스트용) - BE ChargerInfo 매핑 */
export const chargerInfoSchema = z.object({
  uuid: z.string(),
  csms_charger_id: z.string(),
  // 고객사 응답은 내부 식별자를 내려주지 않는다
  charger_model_id: z.number().nullish(),
  serial_number: z.string().nullish(),
  gov_station_id: z.string().nullish(),
  status: chargerStatusSchema,
  installed_on: z.string().nullish(),
  activated_on: z.string().nullish(),
  // 공용 여부 컬럼 — 롤아웃 순서 대비 nullish
  is_public: z.boolean().nullish(),
  // BE에서 추가 예정 필드
  vendor_name: z.string().nullish(),
  model_name: z.string().nullish(),
  firmware_version: z.string().nullish(),
});
export type ChargerInfo = z.infer<typeof chargerInfoSchema>;

/** 커넥터 리스트 항목 (메인 응답 단위) - BE ConnectorListItem 매핑 */
export const connectorListItemSchema = z
  .object({
    uuid: z.string(),
    connector_index: z.number(),
    managed_id: z.string(),
    connector_shape: z.string(),
    // 화면에는 코드가 아니라 이 라벨을 쓴다. 미지정 코드는 코드값이 그대로 담겨 온다
    connector_shape_label: z.string().nullish(),
    rated_capacity: z.union([z.number(), z.string()]),
    gov_connector_id: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
    // OCPP 상태
    ocpp_status: ocppStatusSchema,
    csms_status: csmsStatusSchema.nullish(),
    connection_status: connectionStatusSchema.nullish(),
    charging_status_stale_reason: staleReasonSchema.nullish(),
    status_updated_at: z.string().nullish(),
    last_heartbeat_at: z.string().nullish(),
    evented_at: z.string().nullish(),
    // BE에서 추가 예정
    last_charge_end_at: z.string().nullish(),
    // 중첩 정보
    charger: chargerInfoSchema,
    station: stationInfoSchema,
    // 소유 운영사 — 롤아웃 순서 대비 nullish
    cpo: chargerCpoSchema.nullish(),
    charger_vendor_name: z.string().nullish(),
    charger_model_name: z.string().nullish(),
    // 고객사 응답은 null 이다 (적용 요금제 미노출)
    price_policy: pricePolicyEmbedSchema.nullish(),
  })
  .transform(
    ({
      created_at,
      updated_at,
      status_updated_at,
      last_heartbeat_at,
      last_charge_end_at,
      evented_at,
      rated_capacity,
      charger,
      ...rest
    }) => ({
      ...rest,
      rated_capacity:
        typeof rated_capacity === "string"
          ? parseFloat(rated_capacity)
          : rated_capacity,
      created_at: formatDateKST(created_at),
      updated_at: formatDateKST(updated_at),
      status_updated_at: status_updated_at
        ? formatDateKST(status_updated_at)
        : null,
      last_heartbeat_at: last_heartbeat_at
        ? formatDateKST(last_heartbeat_at)
        : null,
      last_charge_end_at: last_charge_end_at
        ? formatDateKST(last_charge_end_at)
        : null,
      evented_at: evented_at ? formatDateKST(evented_at) : null,
      charger: {
        ...charger,
        installed_on: charger.installed_on
          ? formatDateOnlyKST(charger.installed_on)
          : null,
        activated_on: charger.activated_on
          ? formatDateOnlyKST(charger.activated_on)
          : null,
      },
    }),
  );

export type ConnectorListItem = z.infer<typeof connectorListItemSchema>;

/** 상태별 개수 - BE ChargerStatusCountData 매핑 */
export const chargerStatusCountSchema = z.object({
  available: z.number(),
  preparing: z.number(),
  charging: z.number(),
  charge_complete: z.number(),
  faulted: z.number(),
  unavailable: z.number(),
  no_status: z.number(),
});
export type ChargerStatusCount = z.infer<typeof chargerStatusCountSchema>;

/** 충전기 리스트 응답 - BE ChargerListData 매핑 */
export const responseChargerListDataSchema = z.object({
  items: z.array(connectorListItemSchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
  status_count: chargerStatusCountSchema,
});
export type ResponseChargerListData = z.infer<
  typeof responseChargerListDataSchema
>;

export const responseChargerListSchema = responseChargerListDataSchema;
export type ResponseChargerList = z.infer<typeof responseChargerListSchema>;

// ============================================
// Charger Detail (충전기 상세 조회 응답) - BE ChargerData 매핑
// ============================================

/** 이 충전기를 이용할 수 있는 고객사 */
export const chargerClientContractSchema = z.object({
  name: z.string(),
  /** 전용으로 지정한 고객사. 여럿일 수 있다 */
  is_dedicated: z.boolean(),
});
export type ChargerClientContract = z.infer<typeof chargerClientContractSchema>;

/** 커넥터 모델 스펙 정보 - BE ConnectorModelInfo 매핑. 고객사는 `id`·`uuid` 가 null 이다 */
export const connectorModelInfoSchema = z.object({
  id: z.number().nullish(),
  uuid: z.string().nullish(),
  connector_index: z.number(),
  connector_shape: z.string(),
  connector_shape_label: z.string().nullish(),
  rated_capacity: z.union([z.number(), z.string()]),
});
export type ConnectorModelInfo = z.infer<typeof connectorModelInfoSchema>;

/** 커넥터 정보 (상세용) - BE ConnectorData 매핑. 고객사는 `id`·`charger_id` 가 null 이다 */
export const connectorResultSchema = z.object({
  id: z.number().nullish(),
  uuid: z.string(),
  charger_id: z.number().nullish(),
  csms_charger_id: z.string(),
  connector_index: z.number(),
  managed_id: z.string(),
  gov_connector_id: z.string().nullish(),
  connector_model: connectorModelInfoSchema,
});
export type ConnectorResult = z.infer<typeof connectorResultSchema>;

/** 충전기 상세 응답 - BE ChargerData 매핑 */
export const chargerResultSchema = z
  .object({
    // 내부 식별자 4종(id·cpo_id·station_id·charger_model_id)은 고객사 응답에서 null 이다
    id: z.number().nullish(),
    uuid: z.string(),
    cpo_id: cpoIdSchema.nullish(),
    // 소유 운영사 — 롤아웃 순서 대비 nullish
    cpo: chargerCpoSchema.nullish(),
    station_id: z.number().nullish(),
    station_uuid: z.string().nullish(),
    station_name: z.string().nullish(),
    charger_model_id: z.number().nullish(),
    charger_model_uuid: z.string().nullish(),
    charger_vendor_name: z.string().nullish(),
    charger_model_name: z.string().nullish(),
    csms_charger_id: z.string(),
    serial_number: z.string().nullish(),
    firmware_version: z.string().nullish(),
    gov_station_id: z.string().nullish(),
    status: chargerStatusSchema,
    // 기존 충전기는 전부 공용 — BE 롤아웃 전이면 default 로 메운다
    is_public: z.boolean().default(true),
    // 상태를 받은 적이 없으면 null 이 아니라 NoStatus 가 온다
    recent_status: csmsStatusSchema.nullish(),
    connection_status: connectionStatusSchema.nullish(),
    charging_status_stale_reason: staleReasonSchema.nullish(),
    installed_on: z.string().nullish(),
    activated_on: z.string().nullish(),
    authorize_app: z.boolean().nullish(), // 앱 허용
    authorize_roaming: z.boolean().nullish(), // 로밍 허용
    // 자사(운영사 소속 사업자) 충전카드 허용. 기존 충전기는 전부 true 로 시작한다
    authorize_own_cpo_card: z.boolean().default(true),
    /** 이 충전기를 이용할 수 있는 고객사. 계약 범위로만 판정한다(이용상태·기간 무관) */
    client_contracts: z.array(chargerClientContractSchema).nullish(),

    // 지정 시간대 감속 — 충전기 단위
    is_charging_profile_supported: z.boolean().default(false),
    is_charging_profile_enabled: z.boolean().default(false),
    charging_profile_start_at: z.string().nullish(),
    charging_profile_end_at: z.string().nullish(),
    charging_profile_limit: z.union([z.number(), z.string()]).nullish(),

    // 모뎀 정보
    modem_vendor: z.string().nullish(),
    modem_model: z.string().nullish(),
    modem_serial_number: z.string().nullish(),
    // 통신망 정보
    modem_operator: z.string().nullish(),
    usim_network: z.string().nullish(),
    usim_number: z.string().nullish(),
    usim_plan: z.string().nullish(),
    usim_activated_on: z.string().nullish(),
    // 요금제
    price_policy: pricePolicyEmbedSchema.nullish(),
    // 제조사/모델명 (BE join)
    vendor_name: z.string().nullish(),
    model_name: z.string().nullish(),
    // 커넥터 정보
    connectors: z.array(connectorResultSchema),
    // 타임스탬프
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
    memo: z.string().nullish(),
  })
  .transform(
    ({
      installed_on,
      activated_on,
      usim_activated_on,
      created_at,
      updated_at,
      ...rest
    }) => ({
      ...rest,
      installed_on: installed_on ? formatDateOnlyKST(installed_on) : null,
      activated_on: activated_on ? formatDateOnlyKST(activated_on) : null,
      usim_activated_on: usim_activated_on
        ? formatDateOnlyKST(usim_activated_on)
        : null,
      created_at: created_at ? formatDateKST(created_at) : null,
      updated_at: updated_at ? formatDateKST(updated_at) : null,
    }),
  );

export type ChargerResult = z.infer<typeof chargerResultSchema>;

// ============================================
// Charger Model (GET /charger-model/list) - BE ChargerModelData 매핑
// ============================================

/** 커넥터 모델 스펙 (모델 목록용) - BE ConnectorModelData 매핑 */
export const connectorModelDataSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  connector_index: z.number(),
  connector_shape: z.string(),
  // 모델 목록은 이번 규격 범위 밖이라 라벨이 없을 수 있다 — 없으면 코드값을 쓴다
  connector_shape_label: z.string().nullish(),
  rated_capacity: z.union([z.number(), z.string()]),
});
export type ConnectorModelData = z.infer<typeof connectorModelDataSchema>;

/** 충전기 모델 - BE ChargerModelData 매핑 */
export const chargerModelDataSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  vendor_name: z.string(),
  model_name: z.string(),
  image_url: z.string().nullish(),
  connector_models: z.array(connectorModelDataSchema).default([]),
  // 대상 모델은 서버가 정한다 — 화면에서 제조사·모델명을 비교하지 않는다
  is_charging_profile_supported: z.boolean().default(false),
});
export type ChargerModelData = z.infer<typeof chargerModelDataSchema>;

/** 충전기 모델 목록 응답 */
export const chargerModelListSchema = z.array(chargerModelDataSchema);
export type ChargerModelList = z.infer<typeof chargerModelListSchema>;

// ============================================
// 지정 시간대 감속 (Charging Profile)
// ============================================

export const CHARGING_PROFILE_DEFAULT_START = "17:00:00";
export const CHARGING_PROFILE_DEFAULT_END = "22:00:00";
/** 서버에는 하한이 없다 — 화면이 유일한 방어선이다 */
export const CHARGING_PROFILE_MIN_LIMIT = 1.4;

export const CHARGING_PROFILE_MIN_MESSAGE = `${CHARGING_PROFILE_MIN_LIMIT}kW 이상 입력해 주세요.`;
export const CHARGING_PROFILE_SAME_TIME_MESSAGE =
  "시작 시각과 종료 시각을 다르게 선택해 주세요.";
export const CHARGING_PROFILE_CLAMPED_MESSAGE =
  "입력값이 정격 용량보다 커 자동으로 조정됐어요.";

export const CHARGING_PROFILE_HINT = {
  title: "지정 시간대 감속 설정",
  body: "지정한 시간대에 시작한 충전만 설정한 속도로 제한돼요. 시간대가 끝나면 충전기가 스스로 기존 속도로 돌아와요.",
} as const;

const chargingProfileRequestShape = {
  is_charging_profile_enabled: z.boolean().nullish(),
  charging_profile_start_at: z.string().nullish(),
  charging_profile_end_at: z.string().nullish(),
  // Decimal 은 JSON 에서 문자열로 오간다
  charging_profile_limit: z.string().nullish(),
};

/**
 * 정격 상한은 여기서 보지 않는다 — 정격은 폼 밖(선택한 모델·충전기 응답)에 있어
 * 초과 입력은 `clampChargingProfileLimit` 이 깎는다.
 */
function chargingProfileRefine(
  values: {
    is_charging_profile_enabled?: boolean | null;
    charging_profile_start_at?: string | null;
    charging_profile_end_at?: string | null;
    charging_profile_limit?: string | null;
  },
  ctx: z.RefinementCtx,
) {
  if (!values.is_charging_profile_enabled) return;

  const start = values.charging_profile_start_at;
  const end = values.charging_profile_end_at;
  const limit = values.charging_profile_limit;

  if (!start) {
    ctx.addIssue({
      code: "custom",
      path: ["charging_profile_start_at"],
      message: DEFAULT_ERROR_MESSAGE,
    });
  }
  if (!end) {
    ctx.addIssue({
      code: "custom",
      path: ["charging_profile_end_at"],
      message: DEFAULT_ERROR_MESSAGE,
    });
  }
  // 자정을 넘는 시간대(22:00~02:00)는 허용이고 같은 값만 막는다
  if (start && end && start === end) {
    ctx.addIssue({
      code: "custom",
      path: ["charging_profile_end_at"],
      message: CHARGING_PROFILE_SAME_TIME_MESSAGE,
    });
  }

  if (!limit) {
    ctx.addIssue({
      code: "custom",
      path: ["charging_profile_limit"],
      message: DEFAULT_ERROR_MESSAGE,
    });
    return;
  }
  const parsed = Number(limit);
  if (!Number.isFinite(parsed) || parsed < CHARGING_PROFILE_MIN_LIMIT) {
    ctx.addIssue({
      code: "custom",
      path: ["charging_profile_limit"],
      message: CHARGING_PROFILE_MIN_MESSAGE,
    });
  }
}

/**
 * 설정이 충전기당 하나라 상한은 커넥터 정격 중 가장 작은 값에 맞춘다 —
 * 큰 쪽에 맞춘 상한은 작은 커넥터를 제한하지 못한다.
 */
export function getMinRatedCapacity(
  ratedCapacities: (number | string | null | undefined)[],
): number | null {
  const values = ratedCapacities
    .map(toRatedCapacity)
    .filter((v): v is number => v != null && !Number.isNaN(v));
  return values.length ? Math.min(...values) : null;
}

export const CHARGING_PROFILE_FIELDS = [
  "is_charging_profile_enabled",
  "charging_profile_start_at",
  "charging_profile_end_at",
  "charging_profile_limit",
] as const;

/** 폼 기본값이자 미지원 모델로 바꿨을 때 되돌릴 값 */
export const CHARGING_PROFILE_RESET = {
  is_charging_profile_enabled: false,
  charging_profile_start_at: undefined,
  charging_profile_end_at: undefined,
  charging_profile_limit: undefined,
} as const;

type ChargingProfileValues = {
  is_charging_profile_enabled?: boolean | null;
  charging_profile_start_at?: string | null;
  charging_profile_end_at?: string | null;
  charging_profile_limit?: string | null;
};

/**
 * 변경분 diff 가 아니라 현재 폼 값으로 다시 만든다 — 켠 채 저장할 때는 4필드가 함께
 * 가야 서버 검사가 화면에 보이는 값 그대로 돈다. 꺼져 있으면 플래그만 보낸다(나머지
 * 값은 서버에 남아 다시 켜면 이전 값이 나온다).
 */
export function withChargingProfile(
  params: Record<string, unknown>,
  values: ChargingProfileValues,
  isSupported: boolean,
): Record<string, unknown> {
  const next = { ...params };
  for (const field of CHARGING_PROFILE_FIELDS) delete next[field];

  if (!isSupported) return next;
  if (!values.is_charging_profile_enabled) {
    return { ...next, is_charging_profile_enabled: false };
  }
  return {
    ...next,
    is_charging_profile_enabled: true,
    charging_profile_start_at: values.charging_profile_start_at,
    charging_profile_end_at: values.charging_profile_end_at,
    charging_profile_limit: values.charging_profile_limit,
  };
}

/** 응답의 `"3.00"` → 화면·요청이 쓰는 `"3.0"` */
export function toChargingProfileLimit(
  value?: number | string | null,
): string | undefined {
  const parsed = toRatedCapacity(value);
  return parsed == null || Number.isNaN(parsed) ? undefined : parsed.toFixed(1);
}

export function clampChargingProfileLimit(
  value: string,
  ratedCapacity: number | null,
): { value: string; clamped: boolean } {
  const parsed = Number(value);
  if (!value || ratedCapacity == null || !Number.isFinite(parsed)) {
    return { value, clamped: false };
  }
  if (parsed <= ratedCapacity) return { value, clamped: false };
  return { value: ratedCapacity.toFixed(1), clamped: true };
}

export function getChargingProfileRatio(
  limit?: string | null,
  ratedCapacity?: number | null,
): number | null {
  if (!limit || !ratedCapacity) return null;
  const value = Number(limit);
  if (Number.isNaN(value)) return null;
  return Math.round((value / ratedCapacity) * 100);
}

/** 자정을 넘으면 "다음 날"을 붙여 22:00~02:00 이 20시간이 아님을 드러낸다 */
export function getChargingProfileSummary(
  startAt?: string | null,
  endAt?: string | null,
  limit?: string | null,
): string | null {
  if (!startAt || !endAt || !limit) return null;

  const start = formatHour12(startAt);
  const end = formatHour12(endAt);
  if (!start || !end) return null;

  const endLabel = `${startAt > endAt ? "다음 날 " : ""}${end}`;
  return `${start} ~ ${endLabel}에 시작한 충전은 ${limit}kW로 제한되고, 이후에는 기존 속도로 돌아와요.`;
}

// ============================================
// Charger Create Request - BE ChargerRequest 매핑
// ============================================
export const connectorRequestSchema = z.object({
  connector_index: z.number().min(1),
  // 자동 생성을 폐기했다. 전역 유니크라 중복이면 409
  managed_id: z.string().min(1, DEFAULT_ERROR_MESSAGE).max(50),
  gov_connector_id: z
    .string()
    .max(20)
    .nullish()
    .refine((val) => !val || val.length !== 1, {
      message: "두 자리로 입력해 주세요. (01, 12 등)",
    }),
});
export type ConnectorRequest = z.infer<typeof connectorRequestSchema>;

export const chargerCreateRequestSchema = z.object({
  station_uuid: z.string().nonempty(DEFAULT_ERROR_MESSAGE),
  charger_model_uuid: z.string().nonempty(DEFAULT_ERROR_MESSAGE),
  csms_charger_id: z.string().min(1, DEFAULT_ERROR_MESSAGE).max(50),
  serial_number: z.string().max(100).nullish(),
  status: chargerStatusSchema.default("CLOSE"),
  is_public: z.boolean(),
  installed_on: z.string().nullish(),
  activated_on: z.string().nullish(),
  // 모뎀 정보
  modem_vendor: z.string().max(100).nullish(),
  modem_model: z.string().max(100).nullish(),
  modem_serial_number: z.string().max(100).nullish(),
  // 통신망 정보
  modem_operator: z.string().max(100).nullish(),
  usim_network: z.string().max(100).nullish(),
  usim_number: z.string().max(100).nullish(),
  usim_plan: z.string().max(100).nullish(),
  usim_activated_on: z.string().nullish(),
  // 요금제
  price_policy_uuid: z.string().nonempty(DEFAULT_ERROR_MESSAGE),
  // 이용 설정 — 누가 이 충전기를 쓸 수 있는지
  authorize_app: z.boolean().nullish(), // 앱 허용
  authorize_roaming: z.boolean().nullish(), // 로밍 허용
  authorize_own_cpo_card: z.boolean().nullish(), // 자사 충전카드 허용

  // 메모
  memo: z.string().max(1000).nullish(),
  ...chargingProfileRequestShape,
  // 커넥터 정보
  connectors: z.array(connectorRequestSchema),
});
export type ChargerCreateRequest = z.infer<typeof chargerCreateRequestSchema>;

// 폼 전용 스키마 (UI에서만 사용하는 필드 포함)
export const chargerCreateFormSchema = chargerCreateRequestSchema
  .extend({
    charger_vendor_name: z.string().nonempty(DEFAULT_ERROR_MESSAGE), // 제조사 선택용 (API 전송 시 제거)
    is_public: chargerPublicValueSchema,
  })
  .superRefine(chargingProfileRefine);
export type ChargerCreateForm = z.infer<typeof chargerCreateFormSchema>;

// ============================================
// Charger Update Request - BE ChargerUpdateRequest 매핑
// ============================================
export const connectorUpdateRequestSchema = z.object({
  uuid: z.string(),
  managed_id: z.string().min(1, DEFAULT_ERROR_MESSAGE).max(50),
  gov_connector_id: z
    .string()
    .max(20)
    .nullish()
    .refine((val) => !val || val.length !== 1, {
      message: "두 자리로 입력해 주세요. (01, 12 등)",
    }),
});
export type ConnectorUpdateRequest = z.infer<
  typeof connectorUpdateRequestSchema
>;

export const chargerUpdateRequestSchema = z.object({
  charger_model_uuid: z.string().nullish(),
  station_uuid: z.string().nullish(),
  serial_number: z.string().max(100).nullish(),
  // firmware_version·csms_charger_id 는 수정 대상이 아니다 (화면에서도 읽기 전용).
  // 보내면 서버가 422 로 막는다
  status: chargerStatusSchema.nullish(),
  is_public: z.boolean().nullish(),
  installed_on: z.string().nullish(),
  activated_on: z.string().nullish(),
  // 모뎀 정보
  modem_vendor: z.string().max(100).nullish(),
  modem_model: z.string().max(100).nullish(),
  modem_serial_number: z.string().max(100).nullish(),
  // 통신망 정보
  modem_operator: z.string().max(100).nullish(),
  usim_network: z.string().max(100).nullish(),
  usim_number: z.string().max(100).nullish(),
  usim_plan: z.string().max(100).nullish(),
  usim_activated_on: z.string().nullish(),
  // 요금제
  price_policy_uuid: z.string(),
  // 이용 설정 — 누가 이 충전기를 쓸 수 있는지
  authorize_app: z.boolean().nullish(),
  authorize_roaming: z.boolean().nullish(),
  authorize_own_cpo_card: z.boolean().nullish(),
  // 메모
  memo: z.string().max(1000).nullish(),
  ...chargingProfileRequestShape,
  // 커넥터 정보
  connectors: z.array(connectorUpdateRequestSchema).nullish(),
});
export type ChargerUpdateRequest = z.infer<typeof chargerUpdateRequestSchema>;

// 폼 전용 스키마 (UI에서만 사용하는 필드 포함)
export const chargerUpdateFormSchema = chargerUpdateRequestSchema
  .extend({
    is_public: chargerPublicValueSchema,
  })
  .superRefine(chargingProfileRefine);
export type ChargerUpdateForm = z.infer<typeof chargerUpdateFormSchema>;

// ============================================
// 충전기 일괄 변경 (PATCH /charger/bulk/*)
// ============================================
export const bulkChargerUpdateDataSchema = z.object({
  updated_count: z.number(),
  charger_uuids: z.array(z.string()),
});
export type BulkChargerUpdateData = z.infer<typeof bulkChargerUpdateDataSchema>;

export const chargerBulkPricePolicyRequestSchema = z.object({
  charger_uuids: z.array(z.string()).min(1),
  price_policy_uuid: z.string().min(1, DEFAULT_ERROR_MESSAGE),
});
export type ChargerBulkPricePolicyRequest = z.infer<
  typeof chargerBulkPricePolicyRequestSchema
>;

export const chargerBulkUsageSettingRequestSchema = z.object({
  charger_uuids: z.array(z.string()).min(1),
  is_public: z.boolean(),
  authorize_app: z.boolean(),
  authorize_roaming: z.boolean(),
  authorize_own_cpo_card: z.boolean(),
});
export type ChargerBulkUsageSettingRequest = z.infer<
  typeof chargerBulkUsageSettingRequestSchema
>;

// 폼 전용 스키마 — charger_uuids 는 폼 필드가 아니라 제출 시 채워 넣으므로 검증에서 뺀다
// (두면 기본값 [] 이 항상 min(1) 에 걸린다). is_public 은 처음엔 미선택 상태라 타입은
// optional 로 열어 두고 refine 으로 제출 시 필수화한다
export const chargerBulkUsageSettingFormSchema = chargerBulkUsageSettingRequestSchema
  .omit({ charger_uuids: true })
  .extend({
    is_public: chargerPublicValueSchema.optional(),
  })
  .refine((data) => data.is_public !== undefined, {
    message: DEFAULT_ERROR_MESSAGE,
    path: ["is_public"],
  });
export type ChargerBulkUsageSettingForm = z.infer<
  typeof chargerBulkUsageSettingFormSchema
>;

// ============================================
// Filter State (프론트엔드 스토어용)
// ============================================
export type FilterState = {
  // 검색어
  search_type: ChargerSearchType | null;
  keyword: string | null;
  // 기본 기간 필터 (등록일)
  from_created_at: string | null;
  to_created_at: string | null;
  // 필터
  station_filter: string[] | null;
  vendor_name_filter: string[] | null;
  model_name_filter: string[] | null;
  status_filter: CsmsStatus[] | null;
  connection_status_filter: ConnectionStatus[] | null;
  is_public_filter: ("true" | "false")[] | null;
  /** 조치 필요 충전기만 보기. 충전 상태 필터와 상호배타다 (서버가 422 로 강제) */
  action_required: boolean | null;
  has_gov_id_filter: boolean[] | null;
  charging_speed_filter: string[] | null;
  // 소유 운영사 필터 — MASTER 전용
  cpo_ids: number[] | null;
  // 추가 기간 필터
  from_installed_on: string | null;
  to_installed_on: string | null;
  from_activated_on: string | null;
  to_activated_on: string | null;
  from_updated_at: string | null;
  to_updated_at: string | null;
  from_status_updated_at: string | null;
  to_status_updated_at: string | null;
};

// ============================================
// Filter/Search Options
// ============================================

/** 검색 타입 옵션 */
export const CHARGER_SEARCH_TYPE_OPTIONS: FilterOption[] = [
  { value: "managed_id", label: "관리번호" },
  { value: "csms_charger_id", label: "충전기 ID" },
  { value: "station_name", label: "충전소" },
  { value: "charger_vendor_name", label: "제조사" },
  { value: "charger_model_name", label: "모델명" },
  { value: "connector_shape", label: "커넥터 타입" },
  { value: "serial_number", label: "S/N" },
  { value: "gov_connector_id", label: "환경부 충전기 ID" },
  { value: "price_policy_name", label: "적용 요금제" },
];

/** 이용 설정 토글의 ⓘ 안내. 세 토글이 각각 다른 이용 경로를 여닫아 라벨만으론 구분이 안 된다 */
export const CHARGER_USAGE_HINTS = {
  authorize_app: {
    title: "앱 허용 설정",
    body: "충전 앱을 통해 충전기를 원격으로 사용할 수 있어요.",
  },
  authorize_roaming: {
    title: "로밍 허용 설정",
    body: "로밍 협약이 맺어진 타 충전 사업자의 충전카드 또는 앱을 통해 충전기를 사용할 수 있어요.",
  },
  authorize_own_cpo_card: {
    title: "자사 충전카드 허용 설정",
    body: "CSMS에 등록된 자사 충전카드를 통해 충전기를 사용할 수 있어요.",
  },
} as const;

export const ACTION_REQUIRED_TOOLTIP =
  "충전 상태가 ‘고장’, ‘사용불가’ 이거나, 통신 상태가 ‘통신장애’인 충전기 목록이 조회됩니다.";

/**
 * 고객사 화면에 없는 조회 조건. 보내면 422 다.
 *
 * 운영사(`cpo_ids`)는 여기 없다 — 고객사는 운영사별로 별개 계약을 가져 전용 충전기가
 * 여러 운영사에 걸치므로 운영사로 좁히는 것이 정당한 조회다.
 *
 * 설치일·개통일은 값만 노출이고 필터는 없다 — 목록·엑셀에는 실려 오지만 고객사
 * 화면에 필터 칩이 없고, 조건으로 보내면 422 다.
 */
const CLIENT_HIDDEN_PARAMS = [
  "has_gov_id_filter",
  "from_installed_on",
  "to_installed_on",
  "from_activated_on",
  "to_activated_on",
] as const;

/**
 * 목록 조회 파라미터.
 *
 * 서버가 422 로 막는 조합이 둘 있어 화면 상태를 그대로 흘려보내면 안 된다 —
 * `action_required` + `status_filter` 동시 전송, 고객사의 미노출 조건 전송.
 * UI 가 이미 둘 다 막지만, 소속이 바뀌기 전 세션에 남은 값이 나가면 목록이 통째로
 * 에러가 되므로 요청을 만드는 이 지점에서 한 번 더 비운다.
 */
export const buildChargerListParams = (
  params: Record<string, unknown>,
  { isClient = false }: { isClient?: boolean } = {},
): RequestChargerList => {
  const request = requestChargerListSchema.parse(params);

  if (request.action_required) request.status_filter = null;
  else request.action_required = null;

  if (isClient) {
    for (const key of CLIENT_HIDDEN_PARAMS) request[key] = null;
    if (request.search_type === "price_policy_name") {
      request.search_type = null;
      request.keyword = null;
    }
  }

  return request;
};

/** 환경부 ID 필터 옵션 */
export const GOV_ID_FILTER_OPTIONS: FilterOption[] = [
  { value: "true", label: "입력" },
  { value: "false", label: "미입력" },
];

/** CSMS 상태 필터 옵션 */
export const CSMS_STATUS_OPTIONS: FilterOption[] = [
  { value: "Available", label: "충전가능" },
  { value: "Preparing", label: "준비중" },
  { value: "Charging", label: "충전중" },
  { value: "ChargeComplete", label: "충전완료" },
  { value: "Faulted", label: "고장" },
  { value: "Unavailable", label: "사용불가" },
  { value: "NoStatus", label: "상태없음" },
];

/** 충전속도 필터 옵션 (mock) */
export const CHARGING_SPEED_OPTIONS: FilterOption[] = [
  { value: "fast", label: "급속" },
  { value: "medium", label: "중속" },
  { value: "slow", label: "완속" },
];

/** 상태 버튼 설정 */
export type StatusButtonConfig = {
  key: string;
  label: string;
  statusValue: CsmsStatus | null;
  colorClass: string;
};

/**
 * 상태 버튼. 정상 흐름(충전가능→준비중→충전중→충전완료) 뒤에 이상 상태(고장·사용불가),
 * 마지막에 부재(상태없음) 순서다. 목록과 상태변경이력 모달이 함께 쓴다.
 */
export const STATUS_BUTTON_CONFIG: StatusButtonConfig[] = [
  {
    key: "all",
    label: "전체",
    statusValue: null,
    colorClass: "primary-normal",
  },
  {
    key: "available",
    label: "충전가능",
    statusValue: "Available",
    colorClass: "status-available",
  },
  {
    key: "preparing",
    label: "준비중",
    statusValue: "Preparing",
    colorClass: "status-caution",
  },
  {
    key: "charging",
    label: "충전중",
    statusValue: "Charging",
    colorClass: "status-positive",
  },
  {
    key: "charge_complete",
    label: "충전완료",
    statusValue: "ChargeComplete",
    colorClass: "status-caution",
  },
  {
    key: "faulted",
    label: "고장",
    statusValue: "Faulted",
    colorClass: "status-urgent",
  },
  {
    key: "unavailable",
    label: "사용불가",
    statusValue: "Unavailable",
    colorClass: "status-urgent",
  },
  {
    key: "no_status",
    label: "상태없음",
    statusValue: "NoStatus",
    colorClass: "labels-alternative",
  },
];

export const STATUS_LOG_BUTTON_CONFIG: StatusButtonConfig[] =
  STATUS_BUTTON_CONFIG.filter((config) => config.statusValue !== "NoStatus");

/** 정격용량은 문자열("50.00")로 내려오기도 한다 */
export const toRatedCapacity = (
  value?: number | string | null,
): number | null => {
  if (value == null) return null;
  return typeof value === "string" ? parseFloat(value) : value;
};

/** 충전속도 판별 (정격용량 기준) */
export function getChargingSpeed(ratedCapacity: number): string {
  if (ratedCapacity < 30) {
    return "완속";
  } else {
    if (ratedCapacity > 50) {
      return "급속";
    } else {
      return "중속";
    }
  }
}

/** 상태 카운트에서 전체 수 계산 */
export function getTotalCount(statusCount: Record<string, number>): number {
  return Object.values(statusCount).reduce((sum, v) => sum + v, 0);
}

/** 충전기 운영 상태 옵션 */
export const CHARGER_STATUS_OPTIONS = [
  { value: "OPEN", label: "운영중" },
  { value: "CLOSE", label: "운영중지" },
  { value: "PREPARE", label: "설치중" },
];

// ============================================
// Connector Status Log (상태변경이력)
// ============================================

export const statusLogFilterSchema = csmsStatusSchema.exclude(["NoStatus"]);
export type StatusLogFilter = z.infer<typeof statusLogFilterSchema>;

export const connectorStatusLogCountSchema = chargerStatusCountSchema.omit({
  no_status: true,
});
export type ConnectorStatusLogCount = z.infer<
  typeof connectorStatusLogCountSchema
>;

/** 상태변경이력 조회 요청 */
export const requestConnectorStatusLogSchema = tableSchema.extend(
  z.object({
    csms_charger_id: z.string(),
    connector_index: z.number(),
    start_date: z.string(),
    end_date: z.string(),
    status_filter: z.array(statusLogFilterSchema).nullish(),
  }).shape,
);
export type RequestConnectorStatusLog = z.infer<
  typeof requestConnectorStatusLogSchema
>;

/** '상태 정보 상세' 뱃지. null 이면 뱃지 없이 문구만 그린다 */
export const statusDetailBadgeSchema = z.enum(["ACTION", "INFO"]);
export type StatusDetailBadge = z.infer<typeof statusDetailBadgeSchema>;

/** 상태 정보 상세 — 서버가 OCPP 원문을 운영자용 문구로 옮겨 내려준다 */
export const connectorStatusDetailSchema = z.object({
  text: z.string(),
  badge: statusDetailBadgeSchema.nullish(),
  /** 뱃지 호버 시 노출. 줄바꿈은 \n */
  tooltip: z.string().nullish(),
});
export type ConnectorStatusDetail = z.infer<typeof connectorStatusDetailSchema>;

/** 상태변경이력 항목 */
export const connectorStatusLogItemSchema = z
  .object({
    // 고객사는 null. 행 key 는 테이블이 인덱스로 만들므로 이 값에 기대지 않는다
    id: z.number().nullish(),
    csms_charger_id: z.string(),
    connector_index: z.number(),
    ocpp_status: ocppStatusSchema,
    csms_status: csmsStatusSchema.nullish(),
    error_code: z.string().nullish(),
    // OCPP info. 제조사가 상태 전이 사유를 넣어 에러가 아닌 값도 온다 (구 error_detail)
    status_info: z.string().nullish(),
    vendor_error_code: z.string().nullish(),
    status_detail: connectorStatusDetailSchema.nullish(),
    evented_at: z.string(),
    created_at: z.string(),
  })
  .transform(({ evented_at, created_at, ...rest }) => ({
    ...rest,
    evented_at: formatDateKST(evented_at),
    created_at: formatDateKST(created_at),
  }));
export type ConnectorStatusLogItem = z.infer<
  typeof connectorStatusLogItemSchema
>;

/** 상태변경이력 응답 */
export const responseConnectorStatusLogSchema = z.object({
  items: z.array(connectorStatusLogItemSchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
  status_count: connectorStatusLogCountSchema,
});
export type ResponseConnectorStatusLog = z.infer<
  typeof responseConnectorStatusLogSchema
>;

export const DEFAULT_STATUS_LOG_COUNT: ConnectorStatusLogCount = {
  available: 0,
  preparing: 0,
  charging: 0,
  charge_complete: 0,
  faulted: 0,
  unavailable: 0,
};
